import Big from 'big.js';
import { i18n } from '@/common/locale';
import { isNil } from 'lodash-es';
import { AllUnitOptionValues, ApiUnitMap } from '../constants/unit.options';
import { UnitAbbreviations } from '../constants/unit.abbr';
import { QuantityResult, UnitOverride, ValueOptions } from '../types';
import { CountUnitConverter } from './CountUnitConverter';
import { CountPolicies, CountPolicy } from '../policy';

export abstract class UnitConverter<TUnit extends string> {
  protected abstract units: readonly TUnit[];

  protected abstract unitToBase: Record<TUnit, number>;

  // 🔑 정책 확정 훅: 기본은 enabled
  protected resolveCountPolicy(countPolicy?: CountPolicy): CountPolicy {
    return countPolicy ?? CountPolicies.countConversionEnabled;
  }

  public isValidUnit(unit: string): unit is TUnit {
    return this.units.includes(unit as TUnit);
  }

  // 정밀도 비안전 큰 정수의 big.js 콜드경로(드문 입력 전용 경로, formatBigQuantity)에서 단위→base 비율을 얻기 위해 노출한다.
  public getUnitRatio(unit: string): number | undefined {
    return this.isValidUnit(unit) ? this.unitToBase[unit] : undefined;
  }

  // 콜드경로(드문 입력 전용 경로)에서 카운트 변환 적용 여부를 알기 위해 노출한다.
  // (money 등 converter별 기본 정책 차이를 그대로 반영)
  public isCountConversionEnabled(countPolicy?: CountPolicy): boolean {
    return this.resolveCountPolicy(countPolicy) !== CountPolicies.countConversionDisabled;
  }

  protected getBaseUnitName(): TUnit {
    const baseUnit = this.units.find((unit) => this.unitToBase[unit] === 1);
    if (baseUnit) return baseUnit;

    const [minUnit] = [...this.units].sort((a, b) => this.unitToBase[a] - this.unitToBase[b]);
    return minUnit;
  }

  public convertToUnit(
    value: number,
    fromUnit: string,
    toUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    if (!this.isValidUnit(fromUnit)) {
      return policy(value, fromUnit);
    }

    // 동일 단위는 부동소수점 곱·나누기 없이 그대로 패스 (FP 오차 제거 + 성능)
    if (fromUnit === toUnit) {
      return policy(value, toUnit);
    }

    const baseValue = value * this.unitToBase[fromUnit];

    if (!this.isValidUnit(toUnit)) {
      return policy(baseValue, this.getBaseUnitName());
    }

    return policy(baseValue / this.unitToBase[toUnit], toUnit);
  }

  public convertToBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    if (!this.isValidUnit(fromUnit)) return policy(value, fromUnit);

    // value가 0이면 fromUnit 그대로 반환
    if (value === 0) return policy(value, fromUnit);

    const fromRatio = this.unitToBase[fromUnit];
    const baseValue = value * fromRatio;
    const absBase = Math.abs(baseValue);

    for (let i = this.units.length - 1; i >= 0; i--) {
      const unit = this.units[i];
      const ratio = this.unitToBase[unit];
      if (absBase >= ratio) {
        // 결과 단위가 fromUnit과 동일하면 곱·나누기 없이 원본 그대로 패스 (FP 오차 제거)
        if (unit === fromUnit) return policy(value, unit);
        return policy(baseValue / ratio, unit);
      }
    }

    const smallest = this.units[0];
    if (smallest === fromUnit) return policy(value, smallest);
    return policy(baseValue / this.unitToBase[smallest], smallest);
  }

  /**
   * 기존 convertValueWithUnitList와 동일한 로직으로 큰 단위로만 변환
   * 작은 단위로는 변환하지 않음
   */
  public convertToLargerBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    if (!this.isValidUnit(fromUnit)) return policy(value, fromUnit);

    // value가 0이면 fromUnit 그대로 반환
    if (value === 0) return policy(value, fromUnit);

    const fromRatio = this.unitToBase[fromUnit];
    const baseValue = value * fromRatio;
    const absBase = Math.abs(baseValue);

    // 현재 단위의 인덱스 찾기
    let unitIndex = this.units.findIndex((unit) => unit === fromUnit);
    const startIndex = unitIndex;

    // 큰 단위로만 변환 (작은 단위로는 변환하지 않음)
    while (unitIndex < this.units.length - 1) {
      const nextUnit = this.units[unitIndex + 1];
      const nextUnitBase = this.unitToBase[nextUnit];

      if (absBase >= nextUnitBase) {
        unitIndex++;
      } else {
        break;
      }
    }

    const targetUnit = this.units[unitIndex];
    // 변환 단위가 fromUnit 그대로면 곱·나누기 없이 원본 패스 (FP 오차 제거)
    if (unitIndex === startIndex) return policy(value, targetUnit);

    return policy(baseValue / this.unitToBase[targetUnit], targetUnit);
  }

  // -0 처리 대비용
  static normalizeNegZero(s: string) {
    return s === '-0' ? '0' : s;
  }

  // trailing zeros 제거 유틸
  static joinTrimmed(
    i: string,
    d: string,
    options: Omit<Required<ValueOptions>, 'fractionDigits'> & { fractionDigits?: number },
  ) {
    if (
      isNil(options.fractionDigits) ||
      (!options.preserveDecimalZeros && options.fractionDigits > 0)
    ) {
      const trimmed = d.replace(/0+$/, ''); // ← 끝의 0들 제거
      return trimmed ? `${i}.${trimmed}` : i;
    }

    // 자리수 고정 유지
    return isNil(options.fractionDigits) || options.fractionDigits > 0 ? `${i}.${d}` : i;
  }

  static formatValue(quantity: number, _options: ValueOptions) {
    const options: Omit<Required<ValueOptions>, 'fractionDigits'> & { fractionDigits?: number } = {
      fractionDigits: _options.fractionDigits ?? undefined,
      convertToLocaleString: _options.convertToLocaleString ?? true,
      preserveDecimalZeros: _options.preserveDecimalZeros ?? false,
      roundingMode: _options.roundingMode ?? 0, // NOTE: 기본값은 RoundDown
    };

    // 부동소수점 오차로 인한 미세한 값들을 정수로 정규화
    const absValue = Math.abs(quantity);
    const roundedToInteger = Math.round(quantity);
    const roundedAbs = Math.abs(roundedToInteger);

    // 매우 작은 값은 의미있는 값일 수 있으므로 정규화하지 않음
    const isVerySmall = absValue < 1e-8 && absValue > 0;

    let normalizedValue = quantity;
    if (!isVerySmall) {
      const diff = Math.abs(quantity - roundedToInteger);
      // 0으로 나누기 방지: 상대 오차 계산용 분모는 최소 1
      const denominator =
        absValue > roundedAbs ? (absValue > 1 ? absValue : 1) : roundedAbs > 1 ? roundedAbs : 1;
      const relativeError = diff / denominator;
      if (relativeError < 1e-10) {
        normalizedValue = roundedToInteger;
      }
    }

    let stringNum: string;

    if (!isNil(options.fractionDigits)) {
      // 라운딩 모드 제어가 필요하므로 big.js로 toFixed 수행 — 다른 핫 경로는 모두 native
      stringNum = new Big(normalizedValue).toFixed(options.fractionDigits, options.roundingMode);
    } else {
      // FP trailing artifact 제거: 1.2345678900000001 → 1.23456789 처럼
      // 15자리 유효숫자 안에서 손실 없이 표현 가능하면 축약된 표기를 사용한다.
      // (Big.js의 exact-decimal toString을 native로 대체)
      stringNum = Number.isInteger(normalizedValue)
        ? String(normalizedValue)
        : parseFloat(normalizedValue.toPrecision(15)).toString();
    }

    if (stringNum.includes('e')) {
      // toString이 지수표기를 내놓는 경계값(예: 1e-7)은 big.js로 전체 자릿수 복원.
      // 원본 float(normalizedValue)가 아니라 toPrecision(15)로 FP 잔차를 제거한 stringNum에서
      // 복원해야 big.js 걷어내기 전과 동일하게 동작한다(예: 1.234567e-8을 그대로 전개).
      const big = new Big(stringNum);
      const expStr = big.toExponential();
      const [base, exponentStr] = expStr.split('e');
      const exponent = parseInt(exponentStr, 10);
      const totalDigits = base.replace('.', '').length;
      // 소수 자릿수 상한 20 — big.js 걷어내기 전 div의 기본 소수 자릿수(Big.DP=20)를 그대로 따른다.
      // 그 시절 1e18배 단위 변환 같은 나눗셈 결과는 20자리에서 반올림(halfUp)됐으므로 동일하게 맞춘다.
      const digits = Math.min(Math.max(totalDigits - exponent - 1, 0), 20);
      stringNum = big.toFixed(digits);
    }

    const [intPart, decPart = ''] = stringNum.split('.');

    if (options.convertToLocaleString) {
      // 음수 부호 확인 (intPart가 '-0'이거나 음수일 때)
      const isNegative = intPart.startsWith('-');
      const absIntPart = isNegative ? intPart.slice(1) : intPart;

      // 그룹핑
      const groupedInt = BigInt(absIntPart).toLocaleString(); // 필요 시 'en-US' 등 고정 가능
      const sign = isNegative ? '-' : '';
      const out = UnitConverter.normalizeNegZero(
        UnitConverter.joinTrimmed(`${sign}${groupedInt}`, decPart, options),
      );

      return out;
    }

    // convertToLocaleString === false
    const out = UnitConverter.normalizeNegZero(
      UnitConverter.joinTrimmed(intPart, decPart, options),
    );
    return out;
  }

  /**
   * 정밀도 비안전 큰 정수 문자열(예: API의 90자리 정수)을 float64로 떨어뜨리지 않고
   * big.js로 단위 변환·그룹핑한다. native formatValue는 이미 손실된 float를 받으므로
   * 자릿수를 복구할 수 없다 — 원본 문자열에서 직접 변환해야 손실이 없다.
   *
   * 핫 경로가 아닌 콜드경로(드문 입력 전용 경로). 값 크기가 MAX_SAFE_INTEGER를 넘는 입력에서만 호출.
   * factors는 ChartUnitConverter가 native 라우팅으로 확정한 단위·카운트 단위의 비율이다.
   */
  static formatBigQuantity(
    bigStr: string,
    factors: { fromRatio?: number; toRatio?: number; countRatio?: number },
    _options: ValueOptions,
  ): string {
    const options: Omit<Required<ValueOptions>, 'fractionDigits'> & { fractionDigits?: number } = {
      fractionDigits: _options.fractionDigits ?? undefined,
      convertToLocaleString: _options.convertToLocaleString ?? true,
      preserveDecimalZeros: _options.preserveDecimalZeros ?? false,
      roundingMode: _options.roundingMode ?? 0, // NOTE: 기본값은 RoundDown
    };

    let value = new Big(bigStr);
    // 단위 변환(예: B→KB): from·to 비율은 모두 2·10의 거듭제곱이라 big.js에서 정확하다.
    // 동일 비율(no-op)이면 div를 건너뛴다 — big.js div의 기본 소수 상한(Big.DP=20)에 걸려
    // 고정밀 소수가 잘리는 것을 막기 위함. native convertToUnit의 "동일 단위 패스"와 동일.
    if (
      !isNil(factors.fromRatio) &&
      !isNil(factors.toRatio) &&
      factors.fromRatio !== factors.toRatio
    ) {
      value = value.times(factors.fromRatio).div(factors.toRatio);
    }
    // 카운트 스케일(K/M/G/…): countRatio가 주어지면(카운트 변환 적용) ratio가 1이어도 div를 수행한다.
    // big.js 걷어내기 전 카운트 변환도 동일 단위에서 div(1)로 Big.DP=20 반올림을 거쳤기 때문.
    // 카운트 변환 미적용(disabled)이면 countRatio가 undefined로 들어와 div를 건너뛴다.
    if (!isNil(factors.countRatio)) {
      value = value.div(factors.countRatio);
    }

    // native formatValue(상단)와 동일한 near-integer 정규화. 거대한 값에서 크기 대비 무시할
    // 만한 소수부(나눗셈 잔차)는 정수로 흡수한다 — big.js 걷어내기 전 동작과 일치.
    // 예) 1e71 규모 값의 .345는 상대오차가 1e-10 미만이라 정수로 정규화한다.
    const rounded = value.round(0, 1); // round(0 dp, halfUp)
    const absValue = value.abs();
    const roundedAbs = rounded.abs();
    const denominator = absValue.gt(roundedAbs)
      ? absValue.gt(1)
        ? absValue
        : new Big(1)
      : roundedAbs.gt(1)
        ? roundedAbs
        : new Big(1);
    const relativeError = value.minus(rounded).abs().div(denominator);
    const isVerySmall = absValue.lt(1e-8) && absValue.gt(0);
    if (!isVerySmall && relativeError.lt(1e-10)) {
      value = rounded;
    }

    // toFixed()는 toString과 달리 지수표기 없이 전체 자릿수를 보존한다.
    const stringNum = isNil(options.fractionDigits)
      ? value.toFixed()
      : value.toFixed(options.fractionDigits, options.roundingMode);

    const [intPart, decPart = ''] = stringNum.split('.');

    if (options.convertToLocaleString) {
      const isNegative = intPart.startsWith('-');
      const absIntPart = isNegative ? intPart.slice(1) : intPart;
      // BigInt 그룹핑 — 임의 자릿수 정수를 손실 없이 thousands 구분자로 묶는다.
      const groupedInt = BigInt(absIntPart).toLocaleString();
      const sign = isNegative ? '-' : '';
      return UnitConverter.normalizeNegZero(
        UnitConverter.joinTrimmed(`${sign}${groupedInt}`, decPart, options),
      );
    }

    return UnitConverter.normalizeNegZero(UnitConverter.joinTrimmed(intPart, decPart, options));
  }

  static getUnitAbbreviation(unit: string) {
    if (unit === '' || unit === 'none') return '';
    return UnitAbbreviations[unit] ?? unit;
  }

  static formatResult({
    quantity,
    countUnit,
    unit,
    displayUnit,
  }: {
    quantity: string;
    countUnit: string;
    unit: string;
    displayUnit?: string;
  }): string {
    const unitPart = UnitConverter.getUnitAbbreviation(unit) ?? unit;

    if (displayUnit) {
      return [`${quantity}${countUnit}`, displayUnit].filter(Boolean).join(' ');
    }
    return [`${quantity}${countUnit}`, unitPart].filter(Boolean).join(' ');
  }

  static isUnitScaleToInvalid(unit: string): boolean {
    return unit === 'none' || unit === 'won (₩)' || unit === 'yen (¥)' || unit === 'yuan (¥)';
  }

  static convertApiUnit(unit: string): AllUnitOptionValues {
    try {
      return ApiUnitMap[unit];
    } catch {
      console.warn(`new unit: ${unit}`);
      return 'none';
    }
  }

  static getResolvedUnit(defaultUnit: string, unitOverride?: UnitOverride) {
    if (!unitOverride || !unitOverride.use) {
      return UnitConverter.getUnitAbbreviation(defaultUnit);
    }
    const { overrideUnit, autoScaleUnit, displayUnit, unitScaleTo } = unitOverride.item;
    if (overrideUnit === 'none' && displayUnit) {
      return displayUnit;
    }
    if (!overrideUnit || overrideUnit === 'none') {
      return UnitConverter.getUnitAbbreviation(defaultUnit);
    }
    if (autoScaleUnit) {
      return `${UnitConverter.getUnitAbbreviation(overrideUnit)} (${i18n.global.t('WORD.AUTO')})`;
    }
    if (displayUnit) {
      return displayUnit;
    }
    if (unitScaleTo) {
      return UnitConverter.getUnitAbbreviation(unitScaleTo);
    }
    return UnitConverter.getUnitAbbreviation(overrideUnit);
  }

  static getNoneResult(value: number, countPolicy?: CountPolicy): QuantityResult {
    if (countPolicy === CountPolicies.countConversionDisabled) {
      return { quantity: value, countUnit: '', unit: '' };
    }
    const converter = CountUnitConverter.getInstance();
    return converter.convertToBestUnit(value, '');
  }

  public getLargestUnit(units: string[]): string {
    return units.sort((a, b) => this.unitToBase[b] - this.unitToBase[a])[0];
  }
}
